import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { FormInput } from '../../../../components/forms/FormInput';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart } from '../../../../types/Chart/Chart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import { Button } from '../../../../components/buttons/Button';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { NavForward } from '../../../../components/buttons/NavForward';
import 'react-phone-input-2/lib/style.css';
import { FieldInfo } from '../../../../components/FieldInfo';
import * as Sentry from '@sentry/browser';

export const Route = createFileRoute('/_authenticated/$patientId/info/hospitalInfo')({
  component: HospitalInfoPage,
});

function HospitalInfoPage() {
  const { mutate } = useUpdateChartSection();
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [prevMarked, setPrevMarked] = React.useState<boolean>(false);
  const [submitted, setSubmitted] = React.useState<boolean>(false);

  // INITIALIZE FORM
  const hospitalInfoForm = useForm({
    defaultValues: {
      hospital: chart?.DOCTOR_INFO?.hospital ? chart?.DOCTOR_INFO?.hospital : '',
      hospitalStreetAddress: chart?.DOCTOR_INFO?.hospitalStreetAddress ? chart?.DOCTOR_INFO?.hospitalStreetAddress : '',
      hospitalCity: chart?.DOCTOR_INFO?.hospitalCity ? chart?.DOCTOR_INFO?.hospitalCity : '',
      hospitalState: chart?.DOCTOR_INFO?.hospitalState ? chart?.DOCTOR_INFO?.hospitalState : '',
      hospitalZIP: chart?.DOCTOR_INFO?.hospitalZIP ? chart?.DOCTOR_INFO?.hospitalZIP : '',
      hospitalCountry: chart?.DOCTOR_INFO?.hospitalCountry ? chart?.DOCTOR_INFO?.hospitalCountry : '',
    },
    onSubmit: () => {
      handleDrInfoUpdate();
    },
  });

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj && !prevMarked) {
      markPreviousPageCompleted({
        currentPageName: 'hospitalInfo',
        patientInfo,
        value: true,
      });
      setPrevMarked(true);
    }
  }, [patientInfo, prevMarked]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart && patientInfo?.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'hospitalInfo',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  // WRITE FORM DATA TO FIRESTORE
  const handleDrInfoUpdate = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          DOCTOR_INFO: {
            hospital: hospitalInfoForm.state.values.hospital,
            hospitalStreetAddress: hospitalInfoForm.state.values.hospitalStreetAddress,
            hospitalCity: hospitalInfoForm.state.values.hospitalCity,
            hospitalState: hospitalInfoForm.state.values.hospitalState,
            hospitalZIP: hospitalInfoForm.state.values.hospitalZIP,
            hospitalCountry: hospitalInfoForm.state.values.hospitalCountry,
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          setSubmitted(true);
        },
        onError: (error) => {
          Sentry.captureException(error); // SEND ERROR TO SENTRY
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId) {
    return (
      <>
        <div id="photo-upload-page" className="flex flex-col">
          <h1 className="place-self-center text-3xl font-bold">HOSPITAL INFORMATION</h1>
          <div className="my-4">
            <div id="front-id-container" className="flex flex-col items-center">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  hospitalInfoForm.handleSubmit();
                }}
              >
                <div className="flex flex-col">
                  <hospitalInfoForm.Field
                    name="hospital"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Hospital name cannot be empty.';
                        } else if (value.length < 3) {
                          return 'Hospital name must be at least 3 characters long.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Hospital Name*"
                          id="hospital-name"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          // required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <hospitalInfoForm.Field
                    name="hospitalStreetAddress"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'Hospital address cannot be empty.';
                        } else if (value.length < 5) {
                          return 'Hospital name must be at least 5 characters long.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Hospital Street Address*"
                          id="hospital-street-address"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <hospitalInfoForm.Field
                    name="hospitalCity"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'City cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Hospital City*"
                          id="hospital-city"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <hospitalInfoForm.Field
                    name="hospitalState"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'State cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Hospital State*"
                          id="hospital-state"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <hospitalInfoForm.Field
                    name="hospitalZIP"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'ZIP Code cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Hospital ZIP Code*"
                          id="hospital-ZIP"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                  <hospitalInfoForm.Field
                    name="hospitalCountry"
                    validators={{
                      onChange({ value }) {
                        if (!value || value.length === 0) {
                          return 'County cannot be blank.';
                        } else {
                          return undefined;
                        }
                      },
                    }}
                    children={(field) => (
                      <>
                        <FormInput
                          label="Hospital Country*"
                          id="hospital-country"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={true}
                        />
                        <FieldInfo fieldMeta={field.state.meta} />
                      </>
                    )}
                  />
                </div>
                <div className="mt-8 flex flex-col items-center">
                  <hospitalInfoForm.Subscribe
                    selector={(state) => [state.canSubmit, state.errors]}
                    children={([canSubmit]) => (
                      <>
                        {!canSubmit && (
                          <div className="mt-4 flex flex-col items-center">
                            <p className="text-red-500">Please correct any errors above before re-submitting.</p>
                          </div>
                        )}
                        <Button
                          id="submit-hca-info"
                          type="submit"
                          disabled={!canSubmit}
                          onClick={hospitalInfoForm.handleSubmit}
                          textContent="Submit"
                          submitted={submitted}
                        />
                      </>
                    )}
                  />
                </div>
              </form>
            </div>
          </div>
          <div id="nav" className="my-4 flex flex-col items-center">
            {submitted && nextPageURL && <NavForward to={nextPageURL} disabled={false} />}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
